//react
import { useEffect, useState, useRef, useCallback } from "react";

//CSS
import "./TweetGraph.css";
import "../Card.css";

//antd
import { Typography, Popover, Select } from "antd";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";

//utils
import { TOOLTIP_STYLE } from "../../../utils/constants/graphConfigs";
import { generateTreemapTooltip } from "../../../utils/functions/generateTreemapTooltip";

//React echarts
import ReactECharts from "echarts-for-react";

//colors
import { getTwoLineColors } from "../../../utils/functions/colors";

//components
import ErrorLoading from "../../404/ErrorLoading";
import StyledSelect from "../StyledSelect";
import CenterSpin from "../../misc/spin";

//language
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";

const { Text, Title } = Typography;
const { Option } = Select;

/**
 *
 * @param {string} country
 * @returns {JSX} JSX -- Complete Card component contents
 */
export default function TweetGraph({
  data,
  onYearChange,
  onTrimesterChange,
  onCountriesChange,
  setImgUrl,
  title,
}) {
  //language
  const { language } = useCurrentLanguage();

  //useState - options
  const [options, setOptions] = useState(null);

  //useState - selected question
  const [selectedQuestion, setSelectedQuestion] = useState(0);

  //useState - selected option (for select component)
  const [selectedOption, setSelectedOption] = useState("");

  //useState - selected option2
  const [selectedOption2, setSelectedOption2] = useState("");

  //useState - selected countries
  const [selectedCountries, setSelectedCountries] = useState([]);

  //useState - loading
  const [loading, setLoading] = useState(true);

  //useState - tempTitle
  const [tempTitle, setTempTitle] = useState("");

  //useRef - export div image
  const exportDivImageRef = useRef(null);

  //chart Instance
  const chartRef = useRef();

  //useEffect

  const handleCountrySelectChange = (value) => {
    console.log("Selected countries: ", value);
    setSelectedCountries(value);
    onCountriesChange(value);
  };

  const generateSpecificGraphOptions = useCallback(
    (graphType, data, selectedQuestion) => {
      function makeSeriesGroupBar(arr) {
        return arr.map((item, i) => {
          const obj = {
            name: data[selectedQuestion].data[0][i],
            type: "bar",
            itemStyle: {
              borderRadius:
                data[selectedQuestion].graphType === "s_h_bar"
                  ? [0, 0, 0, 0]
                  : data[selectedQuestion].graphType === "g_h_bar"
                  ? [0, 20, 20, 0]
                  : [20, 20, 0, 0],
            },
            stack:
              data[selectedQuestion].graphType === "s_h_bar" ? "total" : false,
            emphasis: {
              focus: "series",
            },
            data: item,
          };
          return obj;
        });
      }

      switch (graphType) {
        
        case "bar":
          return {
            colors: [
              "#C3E0E0",
              "#8FB4B5",
              "#FE7D73",
              "#F1F8E1",
              "#F8B9AB",
              "#FE8980",
            ],
            xAxis: {
              type: "category",
              data: data[selectedQuestion].data[0],
              axisLabel: {
                //interval: 2,
                rotate: data[selectedQuestion].rotate ? 45 : 0,
              },
            },
            yAxis: {
              type: "value",
              axisLabel: {
                formatter: "{value}%",
              },
            },
            series: [
              {
                type: "bar",
                data: data[selectedQuestion].data[1],
                itemStyle: { borderRadius: [20, 20, 0, 0] },
              },
            ],
            tooltip: {
              ...TOOLTIP_STYLE,
              formatter: (params) =>
                `<span>${params.name}</span><br/> ${params.value.toFixed(1)}%`,
            },
          };
        case "line":
          return {
            grid: {
              top: "10%",
              bottom: "10%",
              right: "10%",
              left: "10%",
              height: "80%",
              width: "80%",
            },
            xAxis: {
              type: "category",
              data: data[selectedQuestion].data[0],
              
              axisLabel: {
                rotate: data[selectedQuestion].rotate ? 45 : 0,
                color: "rgba(154, 154, 154, 1)",
              },
              splitLine: {
                show: false,
              },
              axisLine: {
                show: true,
                lineStyle: {
                  color: "rgba(204, 204, 204, 0.6)",
                },
              },
            },
            yAxis: {
              type: "value",
              axisLabel: {
                formatter: `{value}${
                  data[selectedQuestion].isPercentage ? "%" : ""
                }`,
                color: "rgba(154, 154, 154, 1)",
                
              },
              splitLine: {
                show: false,
              },
              axisLine: {
                show: true,
                lineStyle: {
                  color: "rgba(204, 204, 204, 0.6)",
                },
              },
            },
            series: data[selectedQuestion].data.slice(1).map((item, i) => ({
              name: data[selectedQuestion].seriesName?.[i] || "Value",
              type: "line",
              data: item,
              smooth: true,
              lineStyle: { width: 3 },
              showSymbol: false,
            })),
            legend: {
              data: data[selectedQuestion].seriesName || ["Value"],
              top: "2%",
              left: "10%",
              type: "scroll",
            },
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                return `
                            <div>
                            <span>${params[0].axisValueLabel}</span>
                            <div>
                            ${params
                              .map(
                                (param) => `
                                <div style='display: flex; flex-direction: row; justify-content: space-between;'> 
                                <div style='margin-right: 15px'>${
                                  param.marker
                                } ${param.seriesName}</div>
                                <div><b>${param.data.toFixed(1)}%</b></div>
                                            </div>
                                        `
                              )
                              .join("")}
                                    </div>
                                    </div>
                                    `;
              },
            },
          };
        case "treemap":
          return {
            series: {
              type: "treemap",
              data: data[selectedQuestion].data,
              levels: [
                {
                  color: [
                    "rgba(48, 94, 169, 1)",
                    "rgba(48, 94, 169, 0.95)",
                    "rgba(48, 94, 169, 0.90)",
                  ],
                  itemStyle: {
                    borderWidth: 0,
                    gapWidth: 8,
                    borderRadius: 20,
                  },
                },
              ],
            },
            tooltip: {
              ...TOOLTIP_STYLE,
              formatter: (params) => generateTreemapTooltip(params),
            },
          };
        case "sankey":
          return {
            series: {
              type: "sankey",
              layout: "none",
              emphasis: {
                focus: "adjacency",
              },
              left: "130px",
              right: "2%",
              nodeAlign: "left",
              //style labels
              label: {
                show: true,
                position: "left",
                fontFamily: "Open Sans",
                fontSize: 10,
                //shadow none
                shadowColor: "transparent",
              },
              data: data[selectedQuestion].data || [
                { name: "Argentina" }, // Source country
                { name: "Brazil" }, // Target countries
                { name: "Chile" },
                { name: "Uruguay" },
                { name: "Paraguay" },
                { name: "Bolivia" },
                { name: "Peru" },
                { name: "Colombia" },
              ],
              links: data[selectedQuestion].links || [
                { source: "Argentina", target: "Brazil", value: 5 },
                { source: "Argentina", target: "Chile", value: 3 },
                { source: "Argentina", target: "Uruguay", value: 2 },
                { source: "Argentina", target: "Paraguay", value: 1 },
                { source: "Argentina", target: "Bolivia", value: 1 },
                { source: "Argentina", target: "Peru", value: 2 },
                { source: "Argentina", target: "Colombia", value: 2 },
              ],
            },
            tooltip: {
              trigger: "item",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                let text =
                  params.name +
                  ": " +
                  parseFloat(params.value)?.toFixed(2) +
                  "%";

                if (
                  params.data.percentage &&
                  params.data.source !== "Notas sobre migración"
                ) {
                  text =
                    "Notas sobre migración > " +
                    params.data.target +
                    ": " +
                    params.data.value +
                    "%";
                  text +=
                    "<br/>" +
                    params.data.source +
                    " > " +
                    params.data.target +
                    ": " +
                    params.data.percentage +
                    "%";
                }

                return text;
              },
            },
          };
        case "g_v_bar":
          return {
            textStyle: {
              fontFamily: "Open Sans",
            },
            color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              valueFormatter: (value) => {
                return value.toFixed(1) + "%";
              },
              axisPointer: {
                // Use axis to trigger tooltip
                type: "shadow", // 'shadow' as default; can also be 'line' or 'shadow'
              },
              formatter: (props) => {
                const divs = [];

                divs.push("<span>" + props[0].axisValue + "</span></br>");
                props.forEach((line) => {
                  divs.push(
                    "<div>" +
                      line.marker +
                      " " +
                      line.seriesName +
                      ": " +
                      line.data.toFixed(1) +
                      "%</div>"
                  );
                });
                return divs.join("");
              },
            },
            legend: {
              top: "2%",
              left: "10%",
              type: "scroll",
              data:
                data[selectedQuestion].hasOptions === true &&
                selectedOption === "2"
                  ? data[selectedQuestion].data2[0]
                  : data[selectedQuestion].data[0],
            },
            grid: {
              top: "9%",
              left: "5%",
              right: "3%",
              //containLabel: true,
            },
            yAxis: {
              type: "value",
              max: null,
              nameTextStyle: {
                fontFamily: ["Open Sans", "sans-serif"],
                fontSize: 15,
              },
              axisLabel: {
                formatter: "{value}%",
              },
            },
            xAxis: {
              type: "category",
              data: data[selectedQuestion].data[1],
              nameTextStyle: {
                fontFamily: ["Open Sans", "sans-serif"],
                fontSize: 15,
              },
              axisLabel: {
                //interval: 2,
                width: 100,
                overflow: "break",
              },
            },
            series: makeSeriesGroupBar(data[selectedQuestion].data[2]),
          };
        case "line_bar":
          return {
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const dataIndex = params[0].dataIndex; // Get the index of the hovered data point
                const originalDate = data[selectedQuestion].data[0][dataIndex];

                const showItem = (param) => {
                  return `
                                    <div style='display: flex; flex-direction: row; justify-content: space-between;'}}>
                                    <div style='margin-right: 15px'>${
                                      param.marker
                                    } ${param.seriesName}</div>
                                        <div><b>${param.data.toFixed(2)}${
                    data[selectedQuestion].isPercentage ? "%" : ""
                  }</b></div>
                                        </div>
                                        `;
                };

                return `
                            <div>
                            <span>${originalDate}</span>
                                    <div>
                                    ${params
                                      .sort((a, b) => a.data - b.data)
                                      .reverse()
                                      .map((item) => showItem(item))
                                      .join("")}
                                        </div>
                                        </div>
                                        `;
              },
            },
            legend: {
              data: ["Volumen", "Compartidas"],
            },
            grid: {
              left: "40px",
              top: "10%",
              bottom: "10%",
              height: "70%",
              width: "95%",
            },
            xAxis: [
              {
                type: "category",
                data: data[selectedQuestion].data[0],
                axisPointer: {
                  type: "shadow",
                },
              },
            ],
            yAxis: [
              {
                type: "value",
                name: "",
                axisLabel: {
                  formatter: "{value} %",
                },
              },
            ],
            series: [
              {
                name: "Volumen",
                type: "bar",
                tooltip: {
                  valueFormatter: function (value) {
                    return value + " %";
                  },
                },
                itemStyle: { borderRadius: [20, 20, 0, 0] },
                data: data[selectedQuestion].data[1],
              },
              {
                name: "Compartidas",
                type: "line",
                //yAxisIndex: 1,
                lineStyle: {
                  width: 3,
                },
                smooth: true,
                showSymbol: false,
                tooltip: {
                  valueFormatter: function (value) {
                    return value + " %";
                  },
                },
                data: data[selectedQuestion].data[2],
              },
            ],
          };
        case "s_v_bar": {
          return {
            series: data[selectedQuestion].data,
            legend: {
              data: data[selectedQuestion].data.map((item) => item.name),
              top: "2%",
              left: "10%",
              type: "scroll",
            },
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                return `
                            <div>
                                    <span>${params[0].axisValueLabel}</span>
                                    <div>
                                        ${params
                                          .map(
                                            (param) => `
                                            <div style='display: flex; flex-direction: row; justify-content: space-between;'> 
                                                <div style='margin-right: 15px'>${
                                                  param.marker
                                                } ${param.seriesName}</div>
                                                <div><b>${parseFloat(
                                                  param.data
                                                ).toFixed(1)}%</b></div>
                                            </div>
                                        `
                                          )
                                          .join("")}
                                    </div>
                                    </div>
                            `;
              },
            },
            xAxis: {
              type: "category",
              data: data[selectedQuestion].labels,
              axisLabel: {
                formatter: (value, index) => {                  
                  return value;
                },
                //interval: 1,
                rotate: 90,
              },
            },
            yAxis: {
              type: "value",
              axisLabel: {
                formatter: "{value}%",
              },
            },
          };
        }

        default:
          return {};
      }
    },
    [selectedOption]
  );

  const generateGraphOptions = useCallback(
    (graphType, data, selectedQuestion) => {
      // Simplify by moving repeated logic into reusable functions
      switch (graphType) {
        case "bar":
        case "h_bar":
        case "line":
        case "area":
        case "line_bar":
        case "sankey":
        case "treemap":
        case "g_h_bar":
        case "g_v_bar":
        case "s_v_bar":
          const opts = generateSpecificGraphOptions(
            graphType,
            data,
            selectedQuestion
          );
          const color = data[selectedQuestion].useColors
            ? ""
            : getTwoLineColors();
          let newOpts = {};
          if (color !== "") {
            newOpts = {
              ...opts,
              ...color,
            };
          } else {
            newOpts = opts;
          }

          return newOpts;
        default:
          return {};
      }
    },
    [generateSpecificGraphOptions]
  );

  useEffect(() => {
    if (data.length > 0) {
      let tempselectedQuestion = tempTitle === title ? selectedQuestion : 0;
      setSelectedQuestion(tempselectedQuestion);
      const graphType = data[tempselectedQuestion].graphType;
      let opts = generateGraphOptions(graphType, data, tempselectedQuestion);

      setSelectedOption(
        data[tempselectedQuestion].hasOptions
          ? data[tempselectedQuestion].selectedYear
          : ""
      );
      setSelectedOption2(
        data[tempselectedQuestion].hasOptions
          ? data[tempselectedQuestion].selectedTrimester
          : ""
      );
      if (data[tempselectedQuestion].hasCountries) {
        setSelectedCountries(data[tempselectedQuestion].selectedCountries);
      }
      setTempTitle(title);
      setOptions(opts);
      setLoading(false);
      //console.log("TweetGraph data", data);
    }
  }, [
    data,
    language,
    title,
    selectedQuestion,
    tempTitle,
    generateGraphOptions,
  ]);

  const EchartsContent = () => {
    try {
      return (
        <ReactECharts
          option={options}
          style={{ height: "100%", width: "100%", minWidth: "600px" }}
          ref={chartRef}
        />
      );
    } catch (error) {
      return <ErrorLoading language={language} />;
    }
  };

  const triggerDownload = () => {
    const instance = chartRef.current.getEchartsInstance();
    if (!instance || instance._disposed) {
      console.error("Chart instance is disposed or unavailable");
      return;
    }

    const imageUrl = instance.getDataURL({
      type: "png",
      backgroundColor: "#ffffff",
      pixelRatio: 2,
    });

    const link = document.createElement("a");
    link.download = "chart.png";
    link.href = imageUrl;
    link.click();
  };

  return (
    <div
      className="graph_section_container container"
      id={title + "-container"}
      ref={exportDivImageRef}
    >
      <div className="sn-card-left-top">
        <div className="sn-card-title-container">
          <Title
            level={4}
            style={{ color: "var(--blue-primary)", textAlign: "left" }}
          >
            {"" + tempTitle}
          </Title>
          <div className="sn-card-button-container">
            <button
              onClick={triggerDownload}
              className={"downloadButton"}
              style={{
                border: "2px var(--lightblue-primary) solid",
                backgroundColor: "var(--lightblue-primary)",
                color: "white",
                borderRadius: "40px",
                cursor: "pointer",
                marginRight: "10px",
                minWidth: "130px",
              }}
            >
              <DownloadOutlined style={{ marginRight: "10px" }} />
              {language === "en" ? "Download" : "Descargar"}
            </button>
            <Popover
              content={
                <div
                  style={{
                    width: "500px",
                    padding: "5px 16px 4px",
                    textAlign: "justify",
                  }}
                >
                  <Text>
                    {data.length > 0 ? data[selectedQuestion].description : ""}
                  </Text>
                </div>
              }
              title={language === "en" ? "Methodology" : "Metodología"}
              placement="leftTop"
            >
              <InfoCircleOutlined
                style={{
                  fontSize: "20px",
                  color: "var(--grey-primary)",
                  opacity: "0.8",
                  marginRight: "10px",
                }}
              />
            </Popover>
          </div>
        </div>
      </div>
      {data.length > 0 && data[selectedQuestion].hasOptions && (
        <div className="">
          <StyledSelect
            selectedValue={selectedOption}
            changeHandler={setSelectedOption}
            options={data[selectedQuestion].years}
            placeHolderText={"Selecciona una opción"}
            labelAttributeName={"label"}
            valueAttributeName={"code"}
            ignoreTranslation={true}
            language={language}
          />
          <StyledSelect
            selectedValue={selectedOption2}
            changeHandler={setSelectedOption2}
            options={data[selectedQuestion].trimesters}
            placeHolderText={"Selecciona una opción"}
            labelAttributeName={"label"}
            valueAttributeName={"code"}
            ignoreTranslation={true}
            language={language}
          />
        </div>
      )}
      {data.length > 0 && data[selectedQuestion].hasCountries && (
        <div className="">
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder={"Please select"}
            value={selectedCountries}
            onChange={handleCountrySelectChange}
          >
            {data[selectedQuestion].countryOptions.map((o) => (
              <Option
                key={o.code}
                value={o.code}
                disabled={
                  selectedCountries.length > 4
                    ? !selectedCountries.includes(o.code)
                    : false
                }
              >
                {o.label}
              </Option>
            ))}
          </Select>
        </div>
      )}
      {options !== null && (
        <div
          id={title + "perception-graph"}
          className={`overflow-x-auto ${
            data.length > 0 && data[selectedQuestion].isLarge
              ? "set-height-large card-graph"
              : "set-height-small card-graph"
          }`}
        >
          {loading ? (
            <div style={{ height: "100%" }}>
              <CenterSpin />
            </div>
          ) : (
            <EchartsContent />
          )}
        </div>
      )}
      {data.length > 0 && data[selectedQuestion].legendLabel && (
        <div style={{ textAlign: "center" }}>
          <p>{data[selectedQuestion].legendLabel}</p>
        </div>
      )}
      <div id={title + "-container-dc"}></div>
    </div>
  );
}
